import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { unzoom } from '@/lib/uiScale'
import { addDays, cn, getActiveLocale, startOfWeek, toISODate } from '@/lib/utils'

/**
 * Dátum vo formáte zvoleného jazyka aplikácie.
 *
 * Natívny `<input type="date">` kreslí prehliadač podľa *svojho* jazyka
 * (anglický Chrome ukáže 15/10/2026 aj v slovenskej aplikácii) a stránka to
 * nijako neovplyvní. Preto vlastné pole: hodnota ostáva ISO `YYYY-MM-DD`
 * ako pri natívnom inpute, len zobrazenie, písanie a kalendárik idú podľa
 * jazyka aplikácie.
 *
 * Písať sa dá v tvare jazyka (15. 10. 2026, 15.10.26, 10/15/2026 v angličtine)
 * aj ISO; prečíta sa pri opustení poľa alebo Enteri.
 */
export default function DateInput({
  value,
  onChange,
  className,
  disabled,
  placeholder,
  'aria-label': ariaLabel,
}: {
  value: string
  onChange: (value: string) => void
  className?: string
  disabled?: boolean
  placeholder?: string
  'aria-label'?: string
}) {
  const { t, i18n } = useTranslation()
  const locale = getActiveLocale()
  const [text, setText] = useState(() => display(value, locale))
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  // Zmena zvonka (šípky, rýchle filtre) alebo prepnutie jazyka.
  useEffect(() => {
    setText(display(value, locale))
  }, [value, locale, i18n.language])

  function commit() {
    const trimmed = text.trim()
    if (!trimmed) {
      if (value) onChange('')
      return
    }
    const parsed = parse(trimmed, locale)
    if (parsed && parsed !== value) onChange(parsed)
    else setText(display(value, locale))
  }

  return (
    <div ref={box} className={cn('relative', className)}>
      <input
        type="text"
        inputMode="numeric"
        className="input w-full pr-8"
        value={text}
        disabled={disabled}
        aria-label={ariaLabel}
        placeholder={placeholder ?? pattern(locale)}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          }
        }}
      />
      <button
        type="button"
        tabIndex={-1}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="absolute inset-y-0 right-0 flex items-center px-2 text-slate-500 hover:text-slate-800"
        aria-label={t('reservation.date')}
      >
        <CalendarDays size={15} />
      </button>
      {open ? (
        <Picker
          anchor={box.current}
          value={value}
          locale={locale}
          onPick={(day) => {
            onChange(day)
            setOpen(false)
          }}
          onClose={() => setOpen(false)}
          todayLabel={t('global.today')}
        />
      ) : null}
    </div>
  )
}

/** Kalendárik - v portáli s pevnou pozíciou, aby ho neorezal modál. */
function Picker({
  anchor,
  value,
  locale,
  onPick,
  onClose,
  todayLabel,
}: {
  anchor: HTMLElement | null
  value: string
  locale: string
  onPick: (day: string) => void
  onClose: () => void
  todayLabel: string
}) {
  const panel = useRef<HTMLDivElement>(null)
  const initial = value ? new Date(`${value}T00:00:00`) : new Date()
  const [month, setMonth] = useState(new Date(initial.getFullYear(), initial.getMonth(), 1))
  const [pos, setPos] = useState({ top: 0, left: 0 })

  useLayoutEffect(() => {
    if (!anchor) return
    const rect = anchor.getBoundingClientRect()
    const height = panel.current?.offsetHeight ?? 300
    const below = rect.bottom + 4 + height <= window.innerHeight
    setPos({
      top: unzoom(below ? rect.bottom + 4 : Math.max(4, rect.top - 4 - height)),
      left: unzoom(Math.min(rect.left, window.innerWidth - 264)),
    })
  }, [anchor])

  useEffect(() => {
    const away = (e: MouseEvent) => {
      const target = e.target as Node
      if (!panel.current?.contains(target) && !anchor?.contains(target)) onClose()
    }
    const key = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    const scroll = (e: Event) => {
      if (!panel.current?.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', key)
    window.addEventListener('scroll', scroll, true)
    window.addEventListener('resize', onClose)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', key)
      window.removeEventListener('scroll', scroll, true)
      window.removeEventListener('resize', onClose)
    }
  }, [anchor, onClose])

  const first = startOfWeek(month)
  const days = Array.from({ length: 42 }, (_, i) => addDays(first, i))
  const today = toISODate(new Date())
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short' })
  const title = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(month)

  return createPortal(
    <div
      ref={panel}
      style={{ top: pos.top, left: pos.left }}
      className="fixed z-[1000] w-64 rounded-md border border-slate-200 bg-white p-2 text-sm shadow-lg"
    >
      <div className="mb-1 flex items-center justify-between">
        <button
          type="button"
          className="rounded p-1 hover:bg-slate-100"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
        >
          <ChevronLeft size={16} />
        </button>
        <span className="font-medium first-letter:uppercase">{title}</span>
        <button
          type="button"
          className="rounded p-1 hover:bg-slate-100"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
        >
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-7 text-center">
        {days.slice(0, 7).map((day) => (
          <span key={day.getDay()} className="py-1 text-[11px] uppercase text-slate-400">
            {weekday.format(day).replace('.', '')}
          </span>
        ))}
        {days.map((day) => {
          const iso = toISODate(day)
          return (
            <button
              key={iso}
              type="button"
              onClick={() => onPick(iso)}
              className={cn(
                'm-[1px] rounded py-1 tabular-nums',
                day.getMonth() !== month.getMonth() && 'text-slate-300',
                iso === today && 'ring-1 ring-inset ring-slate-400',
                iso === value
                  ? 'bg-action-600 font-semibold text-white'
                  : 'hover:bg-slate-100',
              )}
            >
              {day.getDate()}
            </button>
          )
        })}
      </div>
      <div className="mt-1 text-right">
        <button
          type="button"
          className="rounded px-2 py-1 text-[12px] text-action-700 hover:bg-slate-100"
          onClick={() => onPick(today)}
        >
          {todayLabel}
        </button>
      </div>
    </div>,
    document.body,
  )
}

// ---------------------------------------------------------------------------
// Formát podľa jazyka
// ---------------------------------------------------------------------------
type Part = 'day' | 'month' | 'year'

/** Poradie dňa, mesiaca a roka v danom jazyku (sk: D M R, en: M D R ...). */
function order(locale: string): Part[] {
  return new Intl.DateTimeFormat(locale)
    .formatToParts(new Date(2026, 9, 15))
    .map((p) => p.type)
    .filter((type): type is Part => type === 'day' || type === 'month' || type === 'year')
}

function display(value: string, locale: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return ''
  return new Date(`${value}T00:00:00`).toLocaleDateString(locale)
}

/** Vzor do prázdneho poľa, napr. „dd. mm. rrrr“ - z ukážkového dátumu. */
function pattern(locale: string) {
  const sample = new Date(2026, 9, 15).toLocaleDateString(locale)
  return sample.replace('2026', 'yyyy').replace('15', 'dd').replace('10', 'mm')
}

export function parse(text: string, locale: string): string | null {
  const numbers = text.split(/\D+/).filter(Boolean)
  if (numbers.length !== 3) return null
  let day: number, month: number, year: number
  if (numbers[0].length === 4) {
    ;[year, month, day] = numbers.map(Number)
  } else {
    const parts = order(locale)
    const get = (part: Part) => Number(numbers[parts.indexOf(part)])
    ;[day, month, year] = [get('day'), get('month'), get('year')]
  }
  if (year < 100) year += 2000
  const date = new Date(year, month - 1, day)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null
  }
  return toISODate(date)
}
